import React, { useEffect, useMemo, useState } from "react";
import "./index.css";
import InputMask from "react-input-mask";
import PageControl from "../../components/pageControl";
import ThankYouPage from "../../components/thankYouPage";
import { ReactComponent as BackArrow } from "../../assets/images/back-arrow-pink.svg";
import { ReactComponent as NextArrowGrey } from "../../assets/images/next-arrow-grey.svg";
import { ReactComponent as NextArrowWhite } from "../../assets/images/next-arrow-white.svg";
import { toast } from "react-toastify";
import { translate } from "../../common/localization";
import TermsAndConditions from "../../components/termsAndConditions";
import { getStudyId } from "../../common/localization/helpers";
import { country } from "../../common/localization/globals";

type ErrorCode = 'patientEmailAlreadyExists' | 'patientPhoneAlreadyExists' | 'patientAlreadyExists';

const defaultErrorMessage = "Something went wrong. Please try again later.";
const errorCodesWhitelist = [
  "patientEmailAlreadyExists",
  "patientPhoneAlreadyExists",
  "patientAlreadyExists",
];
const errorCodeToTranslationKeyMap: { [key in ErrorCode]: string } = {
  'patientEmailAlreadyExists': 'patientEmailExists.error',
  'patientPhoneAlreadyExists': 'patientPhoneNumberExists.error',
  'patientAlreadyExists': 'patientExists.error',
};

interface Page {
  id: string;
  title: string;
  question: string;
  type: string;
  customFieldLabel?: string;
  placeholder?: string;
  mask?: Function | string | null;
  validation?: (field: string) => boolean;
  options?: string[];
  expectedOptionsIdx?: number[];
}

interface Answers {
  [key: string]: string;
}

const initialForms: Page[] = [
  {
    id: "name",
    title: "title.1",
    question: "question.name",
    type: "text",
    placeholder: "placeholder.name",
    // If we want to validate fot at least two names
    // validation: (name: string) => name.trim().split(" ").length >= 2,
    validation: (name: string) => !!name.trim(),
  },
  {
    id: "phone",
    title: "title.2",
    question: "question.phone",
    type: "text",
    placeholder: "placeholder.phone",
    mask: translate("phone.mask"),
    validation: (phone: string) => !!phone.match(translate("phone.validation")),
  },
  {
    id: "email",
    title: "title.2",
    question: "question.email",
    type: "text",
    placeholder: "placeholder.email",
    validation: (email: string) =>
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        email
      ),
  },
  {
    id: "postalCode",
    title: "title.3",
    question: "question.postalCode",
    type: "text",
    placeholder: "placeholder.postalCode",
    mask: translate("postalCode.mask"),
    validation: (postalCode: string) =>
      !!postalCode.match(translate("postalCode.validation")),
  },
];

const questions: Page[] = [
  {
    id: "question5",
    title: "title.4",
    question: "question.5",
    type: "options",
    options: ["yes", "no"],
    validation: (answer: string) => !!answer,
    expectedOptionsIdx: [0],
  },
  {
    id: "question6",
    title: "title.4",
    question: "question.6",
    type: "options",
    options: ["yes", "no"],
    validation: (answer: string) => !!answer,
    expectedOptionsIdx: [0],
  },
  {
    id: "question7",
    title: "title.4",
    question: "question.7",
    type: "options",
    options: ["yes", "no"],
    validation: (answer: string) => !!answer,
    expectedOptionsIdx: [0],
  },
  {
    id: "question8",
    title: "title.4",
    question: "question.8",
    type: "options",
    options: ["yes", "no"],
    validation: (answer: string) => !!answer,
  },
  {
    id: "question9",
    title: "title.5",
    question: "question.9",
    type: "options",
    options: ["yes", "no", "maleOrNotOfChildBearingPotential"],
    validation: (answer: string) => !!answer,
    expectedOptionsIdx: [1, 2],
  },
  {
    id: "question10",
    title: "title.5",
    question: "question.10",
    type: "options",
    options: ["yes", "no", "maleOrNotOfChildBearingPotential"],
    validation: (answer: string) => !!answer,
  },
];

const Content = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [forms, setForms] = useState(initialForms);
  const pages = useMemo(() => [...forms, ...questions], [forms]);
  const isSignupPage = currentPage === forms.length;
  const isQuestionsFirstPage = currentPage === forms.length + 1;
  const [answers, setAnswers] = useState({} as Answers);
  const [englishAnswers, setEnglishAnswers] = useState({} as Answers);
  const [isSigningUpPatient, setIsSigningUpPatient] = useState(false);
  const [token, setToken] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isSendingQuestionnaire, setIsSendingQuestionnaire] = useState(false);
  const [validationError, setValidationError] = useState("");
  const numberOfPages = pages.length;
  const isLastPage = currentPage === numberOfPages;
  const isFirstPage = currentPage === 1;
  const showGDPRConfirmation = useMemo(
    () => ["es", "ar", "pl", "gb", "it"].some((c) => country === c),
    []
    );

  const dontShowPostalMask = useMemo(
    () => ["gb"].some((c) => country === c),
    []
    );
    
  const [gpdrConfirmation1, setGpdrConfirmation1] = useState(!showGDPRConfirmation)
  const [gpdrConfirmation2, setGpdrConfirmation2]= useState(!showGDPRConfirmation);

  useEffect(() => {
    if (dontShowPostalMask) {
      const updatedForms = forms.map(form => {
        if (form.id === 'postalCode') {
          return { ...form, mask: null };
        }
        return form;
      });
      setForms(updatedForms);
    } else {
      setForms(initialForms);
    }
  }, [dontShowPostalMask]);

  const clearErrors = () => setValidationError("");

  const isFetching = isSendingQuestionnaire || isSigningUpPatient;

  const getSignupFormattedData = (answers: Answers) => {
    const { name, phone, email, postalCode } = answers;
    const [firstName, ...lastNameArr] = name.trim().split(" ");
    const lastName = lastNameArr.length ? lastNameArr.join(" ") : " ";
    const formattedPhoneNumber = phone.replace(/\D/g, "");
    const studyId = getStudyId();

    if (!studyId) throw new Error("Study Id not found");

    return {
      firstName,
      lastName,
      email,
      postalCode,
      phone: formattedPhoneNumber,
      country: country.toUpperCase(),
      studyId: +studyId,
      sendPatientEmail: false,
    };
  };

  const signupPatient = async () => {
    const qs = window.location.search;
    let signupAPIUrl = process.env.REACT_APP_SIGNUP_API_URL;
    if (!signupAPIUrl) throw new Error("Signup API not found");
    if (!!qs) {
      signupAPIUrl = signupAPIUrl + qs;
    }
    const signupFormattedData = getSignupFormattedData(answers);

    try {
      setIsSigningUpPatient(true);
      const response = await fetch(signupAPIUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(signupFormattedData),
      });
      const responseBody = await response.json();

      if (response.ok) {
        setToken(responseBody.prescreenerId);
        goToNextPage();
      } else {
        const errorCode: ErrorCode | undefined = responseBody.errorCode as ErrorCode;

        if (errorCode && errorCodeToTranslationKeyMap[errorCode]) {
          const translationKey = errorCodeToTranslationKeyMap[errorCode];
          const translatedErrorMessage = translate(translationKey);
          
          createErrorToastr(translatedErrorMessage);
        } else {
          createErrorToastr(defaultErrorMessage);
        }
      }
    } catch (error) {
      createErrorToastr(defaultErrorMessage);
    } finally {
      setIsSigningUpPatient(false);
    }
  };

  const getDigitalPrescreenerAnswers = () => {
    return questions.map((item, idx) => ({
      id: idx + 1,
      question: translate(item.question),
      answer: answers[item.id]
        ? answers[item.id]
        : translate("answer.notApplicable"),
      englishQuestion: translate(item.question, null, "en-us"),
      englishAnswer:
        (answers[item.id] && englishAnswers[item.id]) ||
        (!answers[item.id] &&
          translate("answer.notApplicable", null, "en-us")) ||
        null,
    }));
  };

  const finishQuestionnaire = async () => {
    const digitalPrescreenerAPIUrl =
      process.env.REACT_APP_DIGITAL_PRESCREENER_API_URL;
    if (!digitalPrescreenerAPIUrl)
      throw new Error("Digital prescreener API not found");
    const questionnaireId = process.env.REACT_APP_QUESTIONNAIRE_ID;
    if (!questionnaireId) throw new Error("Questionnaire Id not found");
    const parsedAnswers = getDigitalPrescreenerAnswers();
    try {
      setIsSendingQuestionnaire(true);
      const response = await fetch(digitalPrescreenerAPIUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          answers: parsedAnswers,
          token,
          questionnaireId: +questionnaireId,
        }),
      });
      if (response.ok) {
        setIsComplete(true);
      } else {
        createErrorToastr(defaultErrorMessage);
      }
    } catch (err) {
      console.error(err);
      createErrorToastr(defaultErrorMessage);
    } finally {
      setIsSendingQuestionnaire(false);
    }
  };

  const createErrorToastr = (error: string) => toast.error(error);

  const goToNextPage = () => {
    setCurrentPage(currentPage + 1);
  };

  const goToPreviousPage = () => {
    clearErrors();
    setCurrentPage(currentPage - 1);
  };

  const page = pages[currentPage - 1];

  const handleOnInputChange = (answer: string, englishAnswer?: string) => {
    setAnswers({
      ...answers,
      [page.id]: answer,
    });

    englishAnswer &&
      setEnglishAnswers({
        ...englishAnswers,
        [page.id]: englishAnswer,
      });
  };

  const isExpectedAnswer = () =>
    !page.expectedOptionsIdx ||
    page.expectedOptionsIdx.some(
      (idx) => page.options && answers[page.id] === translate(page.options[idx])
    );

  const getNextAction = () =>
    (isSignupPage && signupPatient) ||
    ((isLastPage || !isExpectedAnswer()) && finishQuestionnaire) ||
    goToNextPage;

  const handleOnInputKeyDown = (key: string) => {
    if (key === "Enter" && isAnswerValid()) {
      getNextAction()();
    }
  };

  const renderQuestionInput: any = (page: Page) => {
    switch (page.type) {
      case "text":
        return (
          <InputMask
            className={`input${validationError ? " error" : ""}`}
            type="text"
            required
            value={answers[page.id] || ""}
            onChange={(e) => handleOnInputChange(e.target.value)}
            placeholder={translate(page.placeholder || "")}
            disabled={isFetching}
            mask={
              typeof page.mask === "function"
                ? page.mask(answers[page.id])
                : page.mask
            }
            onKeyDown={(e) => handleOnInputKeyDown(e.key)}
          />
        );
      case "options":
        return (
          <div className="options-input">
            {page.options?.map((option, idx) => {
              let currentRef: HTMLButtonElement | null = null;
              const translatedOption = translate(option);
              const englishOption = translate(option, null, "en-us");
              return (
                <button
                  ref={(ref) => (currentRef = ref)}
                  key={idx}
                  tabIndex={-1}
                  type="button"
                  value={translatedOption}
                  className={`${
                    answers[page.id] === translatedOption ? "active" : ""
                  }`}
                  onClick={() =>
                    handleOnInputChange(translatedOption, englishOption)
                  }
                  onKeyDown={(e) => {
                    currentRef?.blur();
                    handleOnInputKeyDown(e.key);
                  }}
                >
                  {translatedOption}
                </button>
              );
            })}
          </div>
        );
    }
  };

  const isAnswerValid = () => {
    const validation = page.validation;
    if (!validation) return true;
    const answer = answers[page.id];
    if (!answer) return false;
    return validation(answer);
  };

  return !isComplete ? (
    <>
      <div className="content">
        <div className="content-container">
          <PageControl
            currentPage={currentPage}
            numberOfPages={numberOfPages}
            completed={isSendingQuestionnaire}
          />
          {isFirstPage ? <h1>{translate("title.firstPage")}</h1> : null}
          <h2>{translate(page.title)}</h2>
          <p>{translate(page.question)}</p>
          {renderQuestionInput(page)}
          {showGDPRConfirmation && isFirstPage && (
              <div className="gdpr-container">
                <div className="gdpr-item">
                  <input 
                    type="checkbox"
                    checked={gpdrConfirmation1}
                    onChange={(e) => setGpdrConfirmation1(e.target.checked)}

                  />
                  
                  <label dangerouslySetInnerHTML={{__html: translate('gdpr.text1')}}></label>
                </div>
                <div className="gdpr-item">
                  <input 
                      type="checkbox"
                      checked={gpdrConfirmation2}
                      onChange={(e) => setGpdrConfirmation2(e.target.checked)}

                    />
                    <label dangerouslySetInnerHTML={{__html: translate('gdpr.text2')}}></label>
                </div>
              </div>
            )}
          {validationError ? (
            <p className="error-message">{validationError}</p>
          ) : null}
        </div>
        <div className="buttons-container">
          <div className="previous-button-container">
            {isFirstPage || isQuestionsFirstPage || isFetching ? null : (
              <button
                className="previous-button"
                onClick={goToPreviousPage}
                tabIndex={-1}
              >
                <BackArrow className="back-arrow" />
                {translate("previous")}
              </button>
            )}
          </div>
          <div className="next-button-container">
            {isLastPage ? (
              <button
                className="next-button"
                onClick={finishQuestionnaire}
                disabled={isFetching}
                tabIndex={-1}
              >
                {translate("finish")}
              </button>
            ) : (
              <button
                className="next-button"
                disabled={
                  !isAnswerValid() ||
                  isFetching || 
                  !gpdrConfirmation1 || 
                  !gpdrConfirmation2
                }
                onClick={getNextAction()}
                tabIndex={-1}
              >
                {translate("next")}
                {!isAnswerValid() || isFetching ? (
                  <NextArrowGrey className="next-arrow" />
                ) : (
                  <NextArrowWhite className="next-arrow" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      <TermsAndConditions />
    </>
  ) : (
    <ThankYouPage />
  );
};

export default Content;
